/**
 * OMON-137 Phase 2: per-user edit modal for `_practitionerRole`.
 *
 * Rendered when an admin clicks a row in the Users & Roles panel. The
 * modal shows the user's current role + dataScope + period; the admin
 * can change role / dataScope / set period.end (soft-delete).
 *
 * Writes go through the OMON-144 `practitioner_role_action` callable
 * — never directly to Firestore. The CF enforces the atomic-batch +
 * audit-pairing + claim-projection invariants in one place.
 *
 * Design pattern: typed Edit modal per FHIR resource type. This file
 * handles only `_practitionerRole`; an eventual `EditPerson.tsx` /
 * `EditBasic.tsx` would follow the same shape (modal-overlay +
 * modal-box + form + save/cancel + status footer) without becoming
 * a schema-driven mega-form.
 */
import { modalContainer } from "../dom";
import { escapeHtml } from "../utils/html";
import type { PractitionerRoleRow } from "./loaders";
import {
  callPractitionerRoleAction,
  isFunctionsError,
  type AccessRoleValue,
  type DataScopeValue,
} from "./cf-client";
import { showToast } from "../catalogue/saveModal";
import { auth } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import { callEnvironmentInviteAction, callRelatedPersonAction, type EnvironmentSpec } from "./cf-client";
import { roleLabel, roleName } from "./access-labels";
import type { EnvironmentAccess, FamilyLink } from "./user-catalogue";
import {
  currentEmails, describeEmailStep, describeFamilyStep, isSharedInbox, linkHasAccess,
  planEmailChange, planFamilyChanges, type EmailPlan,
} from "./family-access-plan";
import { relationshipLabel } from "../clients/family-contacts";
import {
  initialChoices,
  needsExpiry,
  planEnvironmentChanges,
  type EnvironmentChoice,
  type EnvironmentStep,
} from "./environment-plan";

const ROLE_OPTIONS: ReadonlyArray<{ value: AccessRoleValue; label: string }> = [
  { value: "carer", label: "Carer" },
  { value: "planner", label: "Planner" },
  { value: "librarian", label: "Librarian" },
  { value: "admin", label: "Admin" },
  { value: "auditor", label: "Auditor" },
];

const DATA_SCOPE_OPTIONS: ReadonlyArray<{ value: DataScopeValue; label: string }> = [
  { value: "real", label: "real (production data)" },
  { value: "test", label: "test (HTEST-stamped only)" },
  { value: "both", label: "both (Ed in dev)" },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultExpiry(): string {
  return new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);
}

interface EditModalOptions {
  /**
   * The production grant being edited, or NULL for someone who has none —
   * an invited consultant, a colleague added only to demo. ADR-0045 §6: one
   * dialog for everyone, with the production fields present only when there
   * is a production grant to edit.
   */
  row: PractitionerRoleRow | null;
  /** Who this is, and where they already have non-production access. */
  person: {
    name: string | null;
    email: string | null;
    personDocId: string | null;
    environments: EnvironmentAccess[];
    /** OMON-348: every client this person is a family contact of, with
     *  that link's Circle of Support state. Absent/empty for staff-only. */
    family?: FamilyLink[];
  };
  /** Called after a successful save; the panel re-renders. */
  onSaved: () => void;
  /**
   * Person card PR 2b (ADR-0046 §5): render INSIDE the card's Access panel
   * instead of as a dialog of its own. The same sections, the same ONE Save
   * (Ed, 2026-09-18, reaffirmed 2026-09-22). `onClose(changed)` replaces
   * closing the dialog: the card then refreshes if anything was saved, so a
   * partial failure's report is never wiped by a reload while it is showing.
   */
  embed?: { host: HTMLElement; onClose: (changed: boolean) => void };
}

/**
 * Open the edit modal for one `_practitionerRole` row.
 *
 * The modal is wired with:
 *   - Role dropdown (carer / planner / librarian / admin / auditor)
 *   - dataScope dropdown (real / test / both)
 *   - Period.end date picker (blank = open-ended; setting it soft-deletes)
 *   - "End role now" button — sets period.end = today (destructive,
 *     requires confirm via "type the user's identifier")
 *   - Save button (calls CF `assign` if new role flow, `update` here)
 *   - Cancel / Esc / click-overlay to dismiss
 */
export function openEditPractitionerRoleModal(opts: EditModalOptions): void {
  const { row, person, embed } = opts;
  // Embedded in the person card, a save only MARKS the card for refresh; the
  // refresh happens when the editor closes (see `embed`).
  let changed = false;
  const onSaved = embed ? () => { changed = true; } : opts.onSaved;

  const displayName = row?.personName ?? person.name ?? person.email ?? person.personDocId;
  const personLabel = displayName
    ? escapeHtml(displayName)
    : `<code>${escapeHtml(person.personDocId ?? "unknown")}</code>`;

  const currentRole: AccessRoleValue | "" = row?.role ?? "";
  const currentScope: DataScopeValue | "" = row?.dataScope ?? "";
  // Strip the time portion for the date input (HTML5 type="date" wants YYYY-MM-DD).
  const currentPeriodEnd =
    row?.periodEnd != null && row.periodEnd !== ""
      ? row.periodEnd.slice(0, 10)
      : "";

  const roleOptionsHtml = ROLE_OPTIONS.map(
    (o) =>
      `<option value="${o.value}"${o.value === currentRole ? " selected" : ""}>${escapeHtml(o.label)}</option>`,
  ).join("");

  const scopeOptionsHtml = DATA_SCOPE_OPTIONS.map(
    (o) =>
      `<option value="${o.value}"${o.value === currentScope ? " selected" : ""}>${escapeHtml(o.label)}</option>`,
  ).join("");

  // ☠️ ONE dialog, always the same parts (Ed, 2026-09-18). The production
  // half used to be REPLACED by a sentence for someone with no grant, so
  // there were two layouts to keep in step — and the password and role
  // buttons vanished entirely. Everything is always present now; what cannot
  // apply is DISABLED, with a title saying why. A disabled control teaches
  // the shape of the thing; an absent one leaves a hole to explain.
  const noGrant = row === null;
  const disabledIf = (when: boolean, why: string) =>
    when ? ` disabled title="${escapeHtml(why)}"` : "";
  const noGrantWhy = "This person has no production role. Give them one with + Add user.";

  // OMON-151: the access-revocation button follows the grant's status —
  // Active → Suspend, Suspended → Resume, Ended → disabled (reviving an
  // ended role needs a fresh assignment, not a toggle).
  // One footer for everyone. Resume replaces Suspend when the grant is
  // suspended; both are disabled when there is no grant, or when it has
  // already ended (reviving that needs a fresh assignment, not a toggle).
  const endedGrant = row?.status === "ended";
  const suspendWhy = noGrant
    ? noGrantWhy
    : endedGrant
      ? "This role has ended. Give them a new one with + Add user."
      : "";
  const suspendButtonHtml = row?.status === "suspended"
    ? `<button class="btn-primary" id="editRoleResume" title="Restore this user's iOS app access">Resume access</button>`
    : `<button class="btn-danger" id="editRoleSuspend" title="${escapeHtml(suspendWhy || "Block this user from the iOS app (reversible)")}"${disabledIf(noGrant || endedGrant, suspendWhy)}>Suspend from app</button>`;

  // OMON-195: admin password reset. Only when the role maps to a person
  // (personDocId) — the CF resolves that person's Firebase Auth email
  // server-side. "Copy reset link" mints a link to hand over; "Send reset
  // email" mails the user a Firebase reset link.
  // Email-link invitees have NO password (ADR-0044), so these do nothing for
  // them — disabled and said out loud, never hidden.
  const pwWhy = noGrant ? "They sign in with an email link — no password exists to reset." : "";
  const pwSectionHtml = `
          <div class="edit-role-pw">
            <span class="edit-role-label edit-role-section">Password</span>
            <div class="pw-actions">
              <button class="btn-outline" id="editRolePwLink" title="${escapeHtml(pwWhy || "Generate a reset link to copy and hand to the user")}"${disabledIf(!row?.personDocId, pwWhy || noGrantWhy)}>Copy reset link</button>
              <button class="btn-outline" id="editRolePwEmail" title="${escapeHtml(pwWhy || "Email the user a Firebase password-reset link")}"${disabledIf(!row?.personDocId, pwWhy || noGrantWhy)}>Send reset email</button>
            </div>
            <div class="edit-role-status" id="editRolePwStatus" aria-live="polite"></div>
          </div>`;

  // The production half. Absent for someone who has no production grant:
  // their record is still theirs to edit, it simply has nothing to say about
  // production (ADR-0045 §6).
  const productionSectionHtml = `
        <div class="edit-role-form">
          <span class="edit-role-label edit-role-section">Production access</span>
          ${noGrant ? `<p class="edit-role-none">None. Give production access with
            <strong>+ Add user</strong>, which mints the login and grants the role.</p>` : ""}
          <label class="edit-role-field">
            <span class="edit-role-label">Role</span>
            <select id="editRoleRole"${disabledIf(noGrant, noGrantWhy)}>${roleOptionsHtml}</select>
          </label>

          <label class="edit-role-field">
            <span class="edit-role-label">Data scope</span>
            <select id="editRoleScope"${disabledIf(noGrant, noGrantWhy)}>${scopeOptionsHtml}</select>
          </label>

          <label class="edit-role-field">
            <span class="edit-role-label">Period end (blank = open-ended)</span>
            <input id="editRolePeriodEnd" type="date" value="${escapeHtml(currentPeriodEnd)}"${disabledIf(noGrant, noGrantWhy)}>
          </label>

          <div class="edit-role-meta">
            <span><strong>Role doc:</strong> <code>${row ? escapeHtml(row.roleId) : "—"}</code></span>
            <span><strong>Status:</strong> ${row
              ? `<span class="status-badge status-${row.status}">${row.status === "active" ? "Active" : row.status === "suspended" ? "Suspended" : "Ended"}</span>`
              : `<span class="status-badge">None</span>`}</span>
          </div>
        </div>`;

  // The environment half (ADR-0045 §6 write side). Tick an environment to
  // give access, untick to revoke, change the role in place. Filled in once
  // the server has listed the environments it can mint accounts in.
  const environmentSectionHtml = `
        <div class="edit-role-field edit-role-environments">
          <span class="edit-role-label edit-role-section">Environment access</span>
          <div class="add-user-env-list" id="editRoleEnvList" role="group"
               aria-label="Environment access">Loading…</div>
          <label class="edit-role-field" id="editRoleExpiresField" hidden>
            <span class="edit-role-label">Access until</span>
            <input type="date" id="editRoleExpires" value="${defaultExpiry()}" min="${todayIso()}" />
          </label>
          <div class="edit-role-status" id="editRoleEnvStatus" aria-live="polite"></div>
        </div>`;

  // OMON-348: Circle of Support, one tick per client. Always present, like
  // every other part of this dialog (Ed, 2026-09-18) — for someone who is
  // nobody's family contact it says so instead of vanishing.
  const familySectionHtml = `
        <div class="edit-role-field edit-role-environments">
          <span class="edit-role-label edit-role-section">Circle of Support</span>
          <div class="add-user-env-list" id="editRoleFamilyList" role="group"
               aria-label="Circle of Support access"></div>
          <div class="edit-role-status" id="editRoleFamilyStatus" aria-live="polite"></div>
        </div>`;

  const sectionsHtml = `
        ${productionSectionHtml}

        ${environmentSectionHtml}

        ${familySectionHtml}

        ${pwSectionHtml}`;
  const endButtonHtml = `<button class="btn-danger" id="editRoleEnd" title="${escapeHtml(suspendWhy || "Set period.end = now (definitive termination)")}"${disabledIf(noGrant || endedGrant, suspendWhy || "This role has already ended.")}>End role now</button>`;

  if (embed) {
    // Inside the card's Access panel: the card is the frame, so no overlay
    // or title. The destructive pair sits apart on the left of the actions.
    embed.host.innerHTML = `
      <div class="person-card-editing edit-role-modal">
        <div class="person-card-panel-head"><span class="person-card-label">Editing access</span></div>
        ${sectionsHtml}
        <div class="edit-role-status" id="editRoleStatus" aria-live="polite"></div>
        <div class="modal-actions">
          ${suspendButtonHtml}
          ${endButtonHtml}
          <button class="btn-outline" id="editRoleCancel">Cancel</button>
          <button class="btn-primary" id="editRoleSave">Save access</button>
        </div>
      </div>`;
  } else {
    modalContainer.innerHTML = `
    <div class="modal-overlay">
      <div class="modal-box edit-role-modal modal-pinned" role="dialog" aria-labelledby="editRoleTitle">
        <h3 id="editRoleTitle">Edit access</h3>
        <p class="edit-role-subject">${personLabel}</p>

        <div class="modal-scroll">
        ${sectionsHtml}

        </div>

        <!-- Outside the scroll: a Save result must be visible wherever the
             middle is scrolled to. -->
        <div class="edit-role-status" id="editRoleStatus" aria-live="polite"></div>

        <div class="modal-actions">
          <button class="btn-outline" id="editRoleCancel">Cancel</button>
          ${suspendButtonHtml}
          ${endButtonHtml}
          <button class="btn-primary" id="editRoleSave">Save changes</button>
        </div>
      </div>
    </div>
  `;
  }

  const environmentSection = wireEnvironmentSection(person);
  const familySection = wireFamilySection(person.family ?? []);
  // ONE Save applies every half. Each half is tried even if another failed,
  // so one refused step never silently drops the rest; the result is the AND.
  const pendingAccess = () =>
    environmentSection.pending().length + familySection.pending().length;
  const applyAccess = async (): Promise<boolean> => {
    const envOk = await environmentSection.apply();
    const familyOk = await familySection.apply();
    return envOk && familyOk;
  };

  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") cleanup();
  };
  const cleanup = embed
    ? () => embed.onClose(changed)
    : () => {
        modalContainer.innerHTML = "";
        document.removeEventListener("keydown", handleEsc);
      };

  if (!embed) {
    // A dialog of its own: Escape and a click on the overlay dismiss it. In
    // the card, the card owns those.
    document.addEventListener("keydown", handleEsc);
    modalContainer.querySelector(".modal-overlay")?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).classList.contains("modal-overlay")) cleanup();
    });
  }

  modalContainer.querySelector("#editRoleCancel")?.addEventListener("click", cleanup);

  // ── OMON-195: password-reset handlers (present only when personDocId set) ──
  const pwStatus = () => modalContainer.querySelector<HTMLElement>("#editRolePwStatus");
  const pwErrMsg = (err: unknown): string =>
    isFunctionsError(err) ? err.message : "Something went wrong — try again.";

  modalContainer.querySelector("#editRolePwLink")?.addEventListener("click", async () => {
    const s = pwStatus();
    if (!s || !row?.personDocId) return;
    s.textContent = "Generating link…";
    try {
      const res = await callPractitionerRoleAction({
        action: "reset-password-link",
        personDocId: row.personDocId,
      });
      const link = String((res.result as { link?: unknown }).link ?? "");
      await navigator.clipboard.writeText(link);
      s.textContent = "Reset link copied to clipboard.";
    } catch (err) {
      s.textContent = pwErrMsg(err);
    }
  });

  modalContainer.querySelector("#editRolePwEmail")?.addEventListener("click", async () => {
    const s = pwStatus();
    if (!s || !row?.personDocId) return;
    s.textContent = "Sending reset email…";
    try {
      const res = await callPractitionerRoleAction({
        action: "reset-password-link",
        personDocId: row.personDocId,
      });
      const email = String((res.result as { email?: unknown }).email ?? "");
      if (!email) {
        s.textContent = "No email on file for this user.";
        return;
      }
      // The Admin SDK can only mint links; the actual send is the Auth web SDK.
      await sendPasswordResetEmail(auth, email);
      s.textContent = `Reset email sent to ${email}.`;
    } catch (err) {
      s.textContent = pwErrMsg(err);
    }
  });

  // ── Save handler ──────────────────────────────────────────────
  // ONE Save for the whole dialog (Ed, 2026-09-18: two save buttons in one
  // dialog, and a footer that changed shape per person). It applies the
  // production changes, then the environment ones, and says when there were
  // none of either.
  modalContainer.querySelector("#editRoleSave")?.addEventListener("click", async () => {
    const roleSelect = modalContainer.querySelector<HTMLSelectElement>("#editRoleRole");
    const scopeSelect = modalContainer.querySelector<HTMLSelectElement>("#editRoleScope");
    const periodEndInput = modalContainer.querySelector<HTMLInputElement>("#editRolePeriodEnd");
    const statusEl = modalContainer.querySelector<HTMLElement>("#editRoleStatus");
    const saveBtn = modalContainer.querySelector<HTMLButtonElement>("#editRoleSave");
    if (!statusEl || !saveBtn) return;

    const accessChanges = pendingAccess();

    // Someone with no production grant has only the environment half.
    if (!row || !roleSelect || !scopeSelect || !periodEndInput) {
      if (accessChanges === 0) {
        statusEl.textContent = "No changes to save.";
        statusEl.className = "edit-role-status info";
        return;
      }
      saveBtn.disabled = true;
      const ok = await applyAccess();
      onSaved();
      if (ok) {
        cleanup();
        showToast("Access updated.", "success");
      } else {
        saveBtn.disabled = false;
      }
      return;
    }

    const newRole = roleSelect.value as AccessRoleValue;
    const newScope = scopeSelect.value as DataScopeValue;
    const newPeriodEndRaw = periodEndInput.value.trim();

    // Detect what changed. The CF treats unchanged fields as no-ops,
    // so we could blast everything — but sending only the deltas keeps
    // the audit-event's FieldChange list clean.
    const payload: {
      action: "update";
      roleDocId: string;
      role?: AccessRoleValue;
      dataScope?: DataScopeValue;
      periodEnd?: string;
    } = {
      action: "update",
      roleDocId: row.roleId,
    };
    if (newRole !== currentRole) payload.role = newRole;
    if (newScope !== currentScope) payload.dataScope = newScope;
    if (newPeriodEndRaw !== currentPeriodEnd) {
      // Convert YYYY-MM-DD → ISO 8601 with time + UTC tz. Picking
      // end-of-day so the role stays valid through the chosen date.
      if (newPeriodEndRaw === "") {
        // Edge case: blanking a previously-set period.end. Not supported
        // by the current CF (the helper assumes set-or-leave). Surface
        // as an error rather than silently no-op.
        statusEl.textContent =
          "Clearing period.end isn't supported via this dialog — use the CLI or contact engineering.";
        statusEl.className = "edit-role-status error";
        return;
      }
      payload.periodEnd = `${newPeriodEndRaw}T23:59:59.000Z`;
    }

    const onlyDocIdKey = Object.keys(payload).length === 2;
    if (onlyDocIdKey && accessChanges === 0) {
      statusEl.textContent = "No changes to save.";
      statusEl.className = "edit-role-status info";
      return;
    }

    // Environment-only edit on someone who also holds a production grant.
    if (onlyDocIdKey) {
      saveBtn.disabled = true;
      const ok = await applyAccess();
      onSaved();
      if (ok) {
        cleanup();
        showToast("Access updated.", "success");
      } else {
        saveBtn.disabled = false;
      }
      return;
    }

    saveBtn.disabled = true;
    statusEl.textContent = "Saving…";
    statusEl.className = "edit-role-status";

    try {
      const response = await callPractitionerRoleAction(payload);
      const kind = response.result.kind;
      if (kind === "no_changes") {
        statusEl.textContent = "Server reported no changes.";
        statusEl.className = "edit-role-status info";
        saveBtn.disabled = false;
        return;
      }
      const envOk = await applyAccess();
      onSaved();
      if (!envOk) {
        // The role saved; an environment step did not. Stay open on the
        // per-environment report rather than closing over a half-done save.
        statusEl.textContent = "Role updated. Some environment changes failed — see above.";
        statusEl.className = "edit-role-status error";
        saveBtn.disabled = false;
        return;
      }
      cleanup();
      showToast(`Access updated for ${row.personName ?? row.personDocId ?? "user"}.`, "success");
    } catch (err) {
      saveBtn.disabled = false;
      statusEl.className = "edit-role-status error";
      if (isFunctionsError(err)) {
        statusEl.textContent = `${err.code}: ${err.message}`;
      } else {
        statusEl.textContent = `Unexpected error: ${err instanceof Error ? err.message : String(err)}`;
        console.error("editRoleSave unexpected error:", err);
      }
    }
  });

  // ── Suspend / Resume handlers (OMON-151) ─────────────────────
  //
  // Both follow the same shape: confirm → CF call → toast + onSaved.
  // Suspend has a stronger confirmation (the user's iOS app is about to
  // go dark); Resume is lighter (restoring access is low-stakes).
  const dispatchSuspendOrResume = async (
    action: "suspend" | "resume",
    confirmMsg: string,
    buttonId: string,
    successToast: string,
  ): Promise<void> => {
    const statusEl = modalContainer.querySelector<HTMLElement>("#editRoleStatus");
    const btn = modalContainer.querySelector<HTMLButtonElement>(buttonId);
    if (!statusEl || !btn) return;

    if (action === "suspend") {
      // Stronger confirmation for the access-revoking direction. Resume
      // is one-click — restoring access has no downside if the operator
      // clicks it by mistake (the same modal can immediately re-suspend).
      const confirmed = window.confirm(confirmMsg);
      if (!confirmed) return;
    }

    if (!row) return;
    btn.disabled = true;
    statusEl.textContent = action === "suspend" ? "Suspending…" : "Resuming…";
    statusEl.className = "edit-role-status";

    try {
      await callPractitionerRoleAction({ action, roleDocId: row.roleId });
      // Record the save BEFORE closing: embedded in the card, closing is
      // what decides whether the card refreshes.
      onSaved();
      cleanup();
      showToast(successToast, "success");
    } catch (err) {
      btn.disabled = false;
      statusEl.className = "edit-role-status error";
      if (isFunctionsError(err)) {
        statusEl.textContent = `${err.code}: ${err.message}`;
      } else {
        statusEl.textContent = `Unexpected error: ${err instanceof Error ? err.message : String(err)}`;
        console.error(`editRole${action} unexpected error:`, err);
      }
    }
  };

  modalContainer.querySelector("#editRoleSuspend")?.addEventListener("click", () => {
    if (!row) return;
    const subject = row.personName ?? row.personDocId ?? "this user";
    void dispatchSuspendOrResume(
      "suspend",
      `Suspend ${subject} from the iOS app?\n\nThe user will lose access on their next cold start or after ~5 minutes in the background. Firebase Auth identity stays intact — they can be resumed later.`,
      "#editRoleSuspend",
      `${subject} suspended from app.`,
    );
  });

  modalContainer.querySelector("#editRoleResume")?.addEventListener("click", () => {
    if (!row) return;
    const subject = row.personName ?? row.personDocId ?? "this user";
    void dispatchSuspendOrResume(
      "resume",
      "", // no confirmation for resume — restoring access is low-stakes
      "#editRoleResume",
      `${subject} restored to app access.`,
    );
  });

  // ── End-role-now handler ──────────────────────────────────────
  modalContainer.querySelector("#editRoleEnd")?.addEventListener("click", async () => {
    const statusEl = modalContainer.querySelector<HTMLElement>("#editRoleStatus");
    const endBtn = modalContainer.querySelector<HTMLButtonElement>("#editRoleEnd");
    if (!row || !statusEl || !endBtn) return;

    const subject = row.personName ?? row.personDocId ?? "this user";
    const confirmed = window.confirm(
      `End role for ${subject} immediately?\n\nThis sets period.end = now (soft-delete).\nThe user will lose access on their next token refresh (up to 1h).`,
    );
    if (!confirmed) return;

    endBtn.disabled = true;
    statusEl.textContent = "Ending…";
    statusEl.className = "edit-role-status";

    try {
      await callPractitionerRoleAction({
        action: "end",
        roleDocId: row.roleId,
      });
      onSaved();
      cleanup();
      showToast(`Role ended for ${subject}.`, "success");
    } catch (err) {
      endBtn.disabled = false;
      statusEl.className = "edit-role-status error";
      if (isFunctionsError(err)) {
        statusEl.textContent = `${err.code}: ${err.message}`;
      } else {
        statusEl.textContent = `Unexpected error: ${err instanceof Error ? err.message : String(err)}`;
        console.error("editRoleEnd unexpected error:", err);
      }
    }
  });
}

/**
 * ADR-0045 §6 (write side): the environment-access section inside the modal.
 *
 * Tick an environment to give access, untick to take it away, change the role
 * in place. The plan comes from `environment-plan.ts`; this only renders it
 * and calls `training_invite_action` for each step.
 *
 * ☠️ Steps run SEQUENTIALLY and report as they go, like the Add user door:
 * they are server writes against one person, and a failure in the third must
 * not hide that the first two landed.
 */
interface EnvironmentSection {
  /** Steps the ticks imply right now. Empty when nothing would change. */
  pending: () => EnvironmentStep[];
  /** Run them, reporting per environment. True when every step landed. */
  apply: () => Promise<boolean>;
}

function wireEnvironmentSection(person: EditModalOptions["person"]): EnvironmentSection {
  const nothing: EnvironmentSection = { pending: () => [], apply: async () => true };
  const el = <T extends HTMLElement>(id: string) => modalContainer.querySelector<T>(`#${id}`);
  const list = el<HTMLElement>("editRoleEnvList");
  const statusEl = el<HTMLElement>("editRoleEnvStatus");
  const expiresField = el<HTMLElement>("editRoleExpiresField");
  const expiresInput = el<HTMLInputElement>("editRoleExpires");
  if (!list || !statusEl || !expiresField || !expiresInput) return nothing;

  const setStatus = (html: string, isError = false) => {
    statusEl.innerHTML = html;
    statusEl.classList.toggle("edit-role-status-error", isError);
  };

  if (!person.email) {
    list.textContent =
      "No email on file for this person, so no account can be minted for them " +
      "in another environment. Add an email to their person record first.";
    return nothing;
  }

  let specs: EnvironmentSpec[] = [];

  const choices = (): EnvironmentChoice[] =>
    specs.map((spec) => {
      const checked = list.querySelector<HTMLInputElement>(
        `input[data-env="${CSS.escape(spec.environment)}"]`)?.checked ?? false;
      const role = list.querySelector<HTMLSelectElement>(
        `select[data-env="${CSS.escape(spec.environment)}"]`)?.value ?? "";
      return { environment: spec.environment, role: checked ? role : null };
    });

  const sync = () => {
    for (const spec of specs) {
      const check = list.querySelector<HTMLInputElement>(`input[data-env="${CSS.escape(spec.environment)}"]`);
      const select = list.querySelector<HTMLSelectElement>(`select[data-env="${CSS.escape(spec.environment)}"]`);
      if (select) select.disabled = !check?.checked;
    }
    const steps = planEnvironmentChanges(person.environments, choices());
    expiresField.hidden = !needsExpiry(steps);
    expiresInput.required = needsExpiry(steps);
    setStatus(
      steps.length === 0
        ? ""
        : `<div>Will: ${steps.map((s) => escapeHtml(s.describe)).join("; ")}.</div>`,
    );
  };

  const runStep = async (step: EnvironmentStep, expires: string): Promise<string> => {
    if (step.kind === "invite") {
      await callEnvironmentInviteAction({
        action: "invite",
        environment: step.environment,
        role: step.role!,
        email: person.email!,
        ...(step.needsExpiry ? { expires } : {}),
        ...(person.name ? { given: person.name.split(" ")[0] } : {}),
      });
      return `${roleName(step.role!)} in ${step.environment}. They sign in with "Email me a sign-in link".`;
    }
    if (step.kind === "extend") {
      await callEnvironmentInviteAction({
        action: "extend", environment: step.environment, uid: step.uid!, expires,
      });
      return `Access extended to ${expires}.`;
    }
    await callEnvironmentInviteAction({
      action: step.kind, environment: step.environment, uid: step.uid!,
    });
    return step.kind === "revoke" ? "Access revoked (disabled, not deleted)." : "Access restored.";
  };

  const pending = () => planEnvironmentChanges(person.environments, choices());

  const apply = async (): Promise<boolean> => {
    const steps = pending();
    if (steps.length === 0) return true;
    const expires = expiresInput.value;
    if (needsExpiry(steps) && !expires) {
      setStatus("Pick the date their access should run until.", true);
      return false;
    }
    const done: string[] = [];
    let ok = true;
    for (const step of steps) {
      try {
        const message = await runStep(step, expires);
        done.push(`<div><strong>${escapeHtml(step.environment)}</strong> — ${escapeHtml(message)}</div>`);
      } catch (err) {
        ok = false;
        const message = err instanceof Error ? err.message : "That step failed.";
        done.push(`<div><strong>${escapeHtml(step.environment)}</strong> — ${escapeHtml(message)}</div>`);
      }
      setStatus(done.join(""), !ok);
    }
    return ok;
  };

  void (async () => {
    try {
      const envs = await callEnvironmentInviteAction({
        action: "list-environments",
        environment: "training", // required by the envelope, ignored for this action
      });
      // Production is granted as a _practitionerRole by the fields above, not
      // minted here, so only the tenant-backed environments belong in this grid.
      specs = (Array.isArray(envs) ? (envs as EnvironmentSpec[]) : [])
        .filter((e) => e.mechanism === "invite");
      if (specs.length === 0) {
        list.textContent = "No environments are available to grant.";
        return;
      }
      const start = initialChoices(specs.map((e) => e.environment), person.environments);
      const roleFor = (environment: string) =>
        start.find((c) => c.environment === environment)?.role ?? null;
      list.innerHTML = specs
        .map((e) => {
          const env = escapeHtml(e.environment);
          const chosen = roleFor(e.environment);
          const current = person.environments.find((a) => a.environment === e.environment);
          const note = current && current.status !== "active"
            ? ` <span class="status-badge status-suspended">${escapeHtml(current.status)}</span>`
            : "";
          const roles = e.roles
            .map((r) => `<option value="${escapeHtml(r)}" title="${escapeHtml(roleLabel(r))}"${r === (chosen ?? current?.role) ? " selected" : ""}>${escapeHtml(roleName(r))}</option>`)
            .join("");
          return `
            <label class="add-user-env-row">
              <input type="checkbox" class="add-user-env-check" data-env="${env}"${chosen ? " checked" : ""} />
              <span class="add-user-env-name">${env}${note}</span>
              <select class="add-user-env-role" data-env="${env}"${chosen ? "" : " disabled"}
                      aria-label="Role in ${env}">${roles}</select>
            </label>`;
        })
        .join("");
      list.addEventListener("change", sync);
      sync();
    } catch (err) {
      list.textContent = "Couldn't load environments.";
      setStatus(err instanceof Error ? escapeHtml(err.message) : "Couldn't load environments.", true);
    }
  })();

  return { pending, apply };
}

// ── OMON-348: Circle of Support, per client ─────────────────────────────
//
// Access belongs to the RELATIONSHIP (this person × this client), so each
// client they are family of gets its own tick. Same pending/apply shape as
// the environment half, so the dialog's ONE Save runs both.

interface FamilySection {
  /** Everything Save would do here: the email change (or an unusable email,
   *  so Save reports it rather than ignoring it), then access changes. */
  pending: () => unknown[];
  apply: () => Promise<boolean>;
}

const FAMILY_STATE_BADGE: Record<FamilyLink["accessState"], string> = {
  "using": `<span class="scope-badge scope-both">using the app</span>`,
  "granted": `<span class="scope-badge scope-both">access given</span>`,
  "not-granted": "",
  "requested": `<span class="status-badge status-suspended">asked to join</span>`,
  "revoked": `<span class="status-badge status-suspended">access removed</span>`,
};

function wireFamilySection(links: FamilyLink[]): FamilySection {
  const nothing: FamilySection = { pending: () => [], apply: async () => true };
  const list = modalContainer.querySelector<HTMLElement>("#editRoleFamilyList");
  const statusEl = modalContainer.querySelector<HTMLElement>("#editRoleFamilyStatus");
  if (!list || !statusEl) return nothing;

  const setStatus = (html: string, isError = false) => {
    statusEl.innerHTML = html;
    statusEl.classList.toggle("edit-role-status-error", isError);
  };

  if (links.length === 0) {
    list.textContent =
      "Not a family contact of any client. Family contacts come from OneTouch — " +
      "add them to a client's contacts there, and they appear here.";
    return nothing;
  }

  // ☠️ One row per EMAIL: when several different people share this address,
  // whoever reads that inbox can sign in as any of them. Say so HERE, where
  // the office decides — not afterwards.
  const names = Array.from(new Set(links.map((l) => l.name).filter((n): n is string => !!n)));
  const sharedWarning = names.length > 1
    ? `<div class="edit-role-status edit-role-status-error">⚠ ${names.length} people are on file with this one email address (${
        names.map((n) => escapeHtml(n)).join(", ")}). With email sign-in, whoever reads that inbox can sign in as any of them.</div>`
    : "";

  // ADR-0046 amendment 2026-09-21 (c): ONE email box for the person, applied
  // to every contact record of theirs (Ed, 2026-09-22). ☠️ Not on a shared
  // inbox: that row is several people, and one box would change them all.
  const shared = isSharedInbox(links);
  const emails = currentEmails(links);
  const emailHtml = shared
    ? `<div class="ct-hint">Email: ${escapeHtml(emails.join(", ") || "none")} — shared by several people, so it can't be changed from here.</div>`
    : `<label class="edit-role-field">
         <span class="edit-role-label">Email</span>
         <input type="email" id="editRoleFamilyEmail" autocomplete="off" spellcheck="false"
                placeholder="${emails.length === 0 ? "No email on file — add one" : ""}"
                value="${escapeHtml(emails.length === 1 ? emails[0] : "")}" />
         <span class="ct-hint">${links.length > 1 ? `Applies to all ${links.length} clients below. ` : ""}${
           emails.length > 1 ? `On file now: ${escapeHtml(emails.join(", "))}. ` : ""
         }Once set here, OneTouch no longer changes it.</span>
       </label>`;
  const emailInput = (): HTMLInputElement | null =>
    modalContainer.querySelector<HTMLInputElement>("#editRoleFamilyEmail");

  list.innerHTML = emailHtml + sharedWarning + links.map((l) => {
    const rels = l.relationships.map(relationshipLabel).join(" · ") || "contact";
    const who = names.length > 1 && l.name ? ` — ${escapeHtml(l.name)}` : "";
    return `
      <label class="add-user-env-row">
        <input type="checkbox" class="add-user-env-check" data-rp="${escapeHtml(l.relatedPersonDocId)}"${
          linkHasAccess(l) ? " checked" : ""} />
        <span class="add-user-env-name">${escapeHtml(l.clientName ?? "a client")}
          <span class="ct-hint">${escapeHtml(rels)}${who}</span>
          ${FAMILY_STATE_BADGE[l.accessState]}</span>
      </label>`;
  }).join("");

  const ticked = (): Set<string> => new Set(
    Array.from(list.querySelectorAll<HTMLInputElement>("input[data-rp]"))
      .filter((c) => c.checked)
      .map((c) => c.dataset.rp!),
  );
  const emailPlan = (): EmailPlan =>
    shared ? { kind: "none" } : planEmailChange(links, emailInput()?.value ?? "");
  const pending = (): unknown[] => {
    const plan = emailPlan();
    return [...(plan.kind === "none" ? [] : [plan]), ...planFamilyChanges(links, ticked())];
  };
  const describe = () => {
    const plan = emailPlan();
    if (plan.kind === "invalid") return setStatus(escapeHtml(plan.message), true);
    const lines = [
      ...(plan.kind === "step" ? [describeEmailStep(plan.step)] : []),
      ...planFamilyChanges(links, ticked()).map(describeFamilyStep),
    ];
    setStatus(lines.length === 0
      ? ""
      : `<div>Will: ${lines.map((s) => escapeHtml(s)).join("; ")}.</div>`);
  };
  list.addEventListener("change", describe);
  list.addEventListener("input", describe);

  const apply = async (): Promise<boolean> => {
    const plan = emailPlan();
    const steps = planFamilyChanges(links, ticked());
    if (plan.kind === "invalid") {
      setStatus(escapeHtml(plan.message), true);
      return false;
    }
    if (plan.kind === "none" && steps.length === 0) return true;
    const done: string[] = [];
    let ok = true;
    // The email first: a grant in the same Save then goes to the right address.
    if (plan.kind === "step") {
      try {
        await callRelatedPersonAction({
          action: "set-email",
          relatedPersonDocIds: plan.step.relatedPersonDocIds,
          email: plan.step.email,
        });
        done.push(`<div><strong>Email</strong> — set to ${escapeHtml(plan.step.email)}.</div>`);
      } catch (err) {
        ok = false;
        const message = err instanceof Error ? err.message : "Setting the email failed.";
        done.push(`<div><strong>Email</strong> — ${escapeHtml(message)}</div>`);
      }
      setStatus(done.join(""), !ok);
    }
    for (const step of steps) {
      try {
        await callRelatedPersonAction({ action: step.kind, relatedPersonDocId: step.relatedPersonDocId });
        done.push(`<div><strong>${escapeHtml(step.clientName)}</strong> — ${
          step.kind === "grant" ? "access given." : "access removed."}</div>`);
      } catch (err) {
        ok = false;
        const message = err instanceof Error ? err.message : "That step failed.";
        done.push(`<div><strong>${escapeHtml(step.clientName)}</strong> — ${escapeHtml(message)}</div>`);
      }
      setStatus(done.join(""), !ok);
    }
    return ok;
  };

  return { pending, apply };
}
